import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Alert, Badge, Button, Card, Col, Form, Image } from 'react-bootstrap';
import ConfirmDialog from './components/ConfirmDialog';
import Loading from './components/Loading';
import useAdminData, { responseError } from './useAdminData';
import { adminFetch, AdminAuthError } from './adminAuth';
import { formatMoney } from './format';
import ItemMetadata from '../itemmetadata.json';

const CATEGORIES = Object.keys(ItemMetadata).filter(category => category !== 'Default');
const CONDITIONS = ['New', 'Excellent', 'Good', 'Well-used', 'Final season'];

// Form fields and how each is read from the item; the API only accepts these
const TEXT_FIELDS = ['title', 'description', 'category', 'subCategory', 'condition', 'size', 'idOverride', 'qcNotes'];
const NUMBER_FIELDS = ['price', 'inventory'];
const FLAG_FIELDS = ['discountEligible', 'donateProceeds', 'isReviewed', 'isFlagged'];

const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required('Required').max(255, '255 characters or fewer'),
  description: Yup.string().trim().required('Required').max(512, '512 characters or fewer'),
  category: Yup.string().oneOf(CATEGORIES, 'Pick a category'),
  condition: Yup.string().oneOf(CONDITIONS, 'Pick a condition'),
  size: Yup.string().trim().required('Required').max(64, '64 characters or fewer'),
  price: Yup.number().typeError('Enter a price').positive('Must be more than zero').max(9999.99, 'At most $9,999.99'),
  inventory: Yup.number().typeError('Enter a quantity').integer('Whole number').min(1, 'At least 1'),
  idOverride: Yup.string().max(16, '16 characters or fewer'),
  qcNotes: Yup.string().max(256, '256 characters or fewer')
});

export function formValues(item) {
  const values = {};
  TEXT_FIELDS.forEach(name => { values[name] = item[name] || ''; });
  NUMBER_FIELDS.forEach(name => { values[name] = item[name] === null || item[name] === undefined ? '' : String(item[name]); });
  FLAG_FIELDS.forEach(name => { values[name] = !!item[name]; });
  return values;
}

/** Only the fields that differ from the saved item, typed for the API */
export function changedItemFields(values, item) {
  const saved = formValues(item);
  const changes = {};
  TEXT_FIELDS.forEach(name => {
    const value = values[name].trim();
    if (value !== saved[name].trim()) {
      changes[name] = name === 'idOverride' && !value ? null : value;
    }
  });
  NUMBER_FIELDS.forEach(name => {
    if (Number(values[name]) !== Number(saved[name])) {
      changes[name] = Number(values[name]);
    }
  });
  FLAG_FIELDS.forEach(name => {
    if (values[name] !== saved[name]) {
      changes[name] = values[name];
    }
  });
  return changes;
}

function SizeInput({ category, value, onChange, isInvalid }) {
  const meta = ItemMetadata[category] || ItemMetadata.Default;
  if (meta.sizeField === 'select' && meta.sizeOptions) {
    const options = meta.sizeOptions.includes(value) || !value ? meta.sizeOptions : [value, ...meta.sizeOptions];
    return (
      <Form.Control as="select" name="size" value={value} onChange={onChange} isInvalid={isInvalid}>
        {!value && <option value="">Select...</option>}
        {options.map(option => <option key={option}>{option}</option>)}
      </Form.Control>
    );
  }
  return <Form.Control name="size" value={value} onChange={onChange} isInvalid={isInvalid} />;
}

export default function ItemDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { data: item, error, loading, waking, reload } = useAdminData(`/items/${id}`);
  const consigners = useAdminData('/admin/consigners');
  const [message, setMessage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // A category change gives the item a new number; follow it so the address matches the label
  useEffect(() => {
    if (item && item.id && item.id !== id) {
      history.replace(`/admin/items/${item.id}`);
    }
  }, [item, id, history]);

  if (loading && !item) {
    return <Loading waking={waking} />;
  }
  if (error) {
    return (
      <div>
        <Alert variant="danger">{error}</Alert>
        <Link to="/admin/items">Back to items</Link>
      </div>
    );
  }

  const consigner = (consigners.data || []).find(c => c.id === item.consignerId);

  const handleAuthError = err => {
    if (err instanceof AdminAuthError) {
      history.replace('/admin/login');
      return true;
    }
    return false;
  };

  const save = async (values, { setSubmitting }) => {
    setMessage(null);
    const changes = changedItemFields(values, item);
    if (Object.keys(changes).length === 0) {
      setMessage({ variant: 'info', text: 'Nothing changed.' });
      setSubmitting(false);
      return;
    }
    try {
      const response = await adminFetch(`/items/${item.id}`, { method: 'PATCH', body: changes });
      if (!response.ok) {
        throw new Error(await responseError(response));
      }
      setMessage({ variant: 'success', text: 'Saved.' });
      reload();
    } catch (err) {
      if (handleAuthError(err)) {
        return;
      }
      setMessage({ variant: 'danger', text: err.message });
    }
    setSubmitting(false);
  };

  const deleteItem = async () => {
    setDeleting(true);
    try {
      const response = await adminFetch(`/items/${item.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error(await responseError(response));
      }
      history.replace(`/admin/items?consigner=${item.consignerId}`);
    } catch (err) {
      if (handleAuthError(err)) {
        return;
      }
      setDeleting(false);
      setConfirmDelete(false);
      setMessage({ variant: 'danger', text: err.message });
    }
  };

  return (
    <div>
      <Link to="/admin/items" className="small">&larr; All items</Link>
      <div className="d-flex align-items-center flex-wrap mt-1 mb-3">
        <h2 className="mb-0 mr-3">{item.id}</h2>
        <span className="text-muted mr-2">{item.title}</span>
        {item.isFlagged && <Badge variant="danger" className="mr-1">Flagged</Badge>}
        {item.isReviewed && <Badge variant="success">Reviewed</Badge>}
      </div>

      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap align-items-start">
          <div className="mr-4 mb-2">
            <div className="small text-muted">Consigner</div>
            <Link to={`/admin/consigners/${item.consignerId}`}>
              {consigner ? `${consigner.firstName} ${consigner.lastName}` : `#${item.consignerId}`}
            </Link>
            {' · '}<Link to={`/admin/items?consigner=${item.consignerId}`} className="small">their items</Link>
          </div>
          <div className="mr-4 mb-2">
            <div className="small text-muted">Price</div>
            {formatMoney(item.price)}{item.inventory > 1 && ` × ${item.inventory}`}
          </div>
          {(item.images || []).length > 0 && (
            <div className="d-flex flex-wrap">
              {item.images.map(src => (
                <a key={src} href={src} target="_blank" rel="noopener noreferrer" className="mr-2 mb-2">
                  <Image src={src} alt={`${item.title} photo`} thumbnail style={{ height: 96 }} />
                </a>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {message && <Alert variant={message.variant} onClose={() => setMessage(null)} dismissible>{message.text}</Alert>}

      <Formik initialValues={formValues(item)} validationSchema={validationSchema} onSubmit={save} enableReinitialize>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, dirty }) => {
          const meta = ItemMetadata[values.category] || ItemMetadata.Default;
          const invalid = name => touched[name] && !!errors[name];
          const feedback = name => <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>;
          return (
            <Form noValidate onSubmit={handleSubmit}>
              <Card className="mb-3">
                <Card.Header>Item</Card.Header>
                <Card.Body>
                  <Form.Group controlId="item-title">
                    <Form.Label>Title</Form.Label>
                    <Form.Control name="title" value={values.title} onChange={handleChange} onBlur={handleBlur} isInvalid={invalid('title')} />
                    {feedback('title')}
                  </Form.Group>
                  <Form.Group controlId="item-description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" rows={3} name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} isInvalid={invalid('description')} />
                    {feedback('description')}
                  </Form.Group>
                  <Form.Row>
                    <Form.Group as={Col} md={4} controlId="item-category">
                      <Form.Label>Category</Form.Label>
                      <Form.Control as="select" name="category" value={values.category} onChange={handleChange} isInvalid={!!errors.category}>
                        {!CATEGORIES.includes(values.category) && <option value={values.category}>{values.category || 'Select...'}</option>}
                        {CATEGORIES.map(category => <option key={category}>{category}</option>)}
                      </Form.Control>
                      {feedback('category')}
                    </Form.Group>
                    {meta.subCategory && (
                      <Form.Group as={Col} md={4} controlId="item-subCategory">
                        <Form.Label>{meta.subCategory.label}</Form.Label>
                        <Form.Control as="select" name="subCategory" value={values.subCategory} onChange={handleChange}>
                          <option value="">None</option>
                          {meta.subCategory.options.map(option => <option key={option}>{option}</option>)}
                        </Form.Control>
                      </Form.Group>
                    )}
                    <Form.Group as={Col} md={4} controlId="item-condition">
                      <Form.Label>Condition</Form.Label>
                      <Form.Control as="select" name="condition" value={values.condition} onChange={handleChange} isInvalid={!!errors.condition}>
                        {!CONDITIONS.includes(values.condition) && <option value={values.condition}>{values.condition || 'Select...'}</option>}
                        {CONDITIONS.map(condition => <option key={condition}>{condition}</option>)}
                      </Form.Control>
                      {feedback('condition')}
                    </Form.Group>
                  </Form.Row>
                  {values.category !== item.category && (
                    <Alert variant="warning" className="small">
                      Changing the category changes the item number's prefix. If a label is already printed or the item
                      is already in Square, reprint the label and re-sync.
                    </Alert>
                  )}
                  <Form.Row>
                    <Form.Group as={Col} md={4} controlId="item-size">
                      <Form.Label>{meta.sizeLabel || 'Size'}</Form.Label>
                      <SizeInput category={values.category} value={values.size} onChange={handleChange} isInvalid={invalid('size')} />
                      {feedback('size')}
                    </Form.Group>
                    <Form.Group as={Col} md={4} controlId="item-price">
                      <Form.Label>Price ($)</Form.Label>
                      <Form.Control name="price" inputMode="decimal" value={values.price} onChange={handleChange} onBlur={handleBlur} isInvalid={invalid('price')} />
                      {feedback('price')}
                    </Form.Group>
                    <Form.Group as={Col} md={4} controlId="item-inventory">
                      <Form.Label>Quantity</Form.Label>
                      <Form.Control name="inventory" inputMode="numeric" value={values.inventory} onChange={handleChange} onBlur={handleBlur} isInvalid={invalid('inventory')} />
                      {feedback('inventory')}
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col} md={4} controlId="item-idOverride">
                      <Form.Label>Vendor label number</Form.Label>
                      <Form.Control name="idOverride" value={values.idOverride} onChange={handleChange} isInvalid={!!errors.idOverride} />
                      <Form.Text muted>Only for vendors who print their own labels</Form.Text>
                      {feedback('idOverride')}
                    </Form.Group>
                    <Form.Group as={Col} md={8} className="pt-md-4">
                      <Form.Check id="item-discountEligible" name="discountEligible" label="Discount eligible (Sunday price drop)" checked={values.discountEligible} onChange={handleChange} />
                      <Form.Check id="item-donateProceeds" name="donateProceeds" label="Donate proceeds" checked={values.donateProceeds} onChange={handleChange} />
                    </Form.Group>
                  </Form.Row>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header>Drop-off review</Card.Header>
                <Card.Body>
                  <Form.Check id="item-isReviewed" name="isReviewed" label="Reviewed" checked={values.isReviewed} onChange={handleChange} />
                  <Form.Check id="item-isFlagged" name="isFlagged" label="Flagged for follow-up" checked={values.isFlagged} onChange={handleChange} className="mb-2" />
                  <Form.Group controlId="item-qcNotes" className="mb-0">
                    <Form.Label>Review notes</Form.Label>
                    <Form.Control as="textarea" rows={2} name="qcNotes" value={values.qcNotes} onChange={handleChange} isInvalid={!!errors.qcNotes} />
                    {feedback('qcNotes')}
                    <Form.Text muted>A consigner's own edits clear "Reviewed"; yours don't.</Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>

              <div className="d-flex">
                <Button type="submit" disabled={isSubmitting || !dirty}>{isSubmitting ? 'Saving...' : 'Save changes'}</Button>
                <Button variant="outline-danger" className="ml-auto" onClick={() => setConfirmDelete(true)}>Delete item</Button>
              </div>
            </Form>
          );
        }}
      </Formik>

      <ConfirmDialog
        show={confirmDelete}
        title={`Delete ${item.id}?`}
        message={(
          <>
            <p>This removes "{item.title}" from {consigner ? `${consigner.firstName} ${consigner.lastName}'s` : 'the'} consignment. It can't be undone.</p>
            <p className="mb-0"><strong>If this item has already been synced to Square, delete it in Square too</strong>: syncs add and update items but never remove them.</p>
          </>
        )}
        confirmLabel="Delete item"
        busy={deleting}
        onConfirm={deleteItem}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
